'use client';

import { IconArrowRight } from '@/icons/IconArrowRight';
import { CollectionTabProperty } from '@/interfaces/homepage';
import { getTextWidth } from '@/utils/helper';
import { Box, Button } from '@mantine/core';
import { useMediaQuery } from '@mantine/hooks';
import React from 'react';

const CustomTab = ({
  tabs,
  activeTab,
  onTabChange,
  children,
  showArrow,
  dataTestId = 'custom-tab',
}: {
  tabs: CollectionTabProperty[];
  activeTab: CollectionTabProperty;
  onTabChange: (params: CollectionTabProperty) => void;
  children?: React.ReactNode;
  showArrow?: boolean;
  dataTestId?: string;
}) => {
  const isSmall = useMediaQuery('(max-width: 1280px)');
  const isXS = useMediaQuery('(max-width: 768px)');
  const mobile = useMediaQuery('(max-width: 639px)');

  const margin = isXS ? 10 : 40;
  const transform = isXS ? 30 : 40;
  const paddingLeft = 6;

  const getTranslateValue = () => {
    const index = tabs?.findIndex((tab) => tab.tags === activeTab?.tags);
    const font = isSmall ? '18px Figtree' : '24px Figtree';
    if (index > 0) {
      let width = 0;
      for (let i = 0; i < index; i++) {
        width += getTextWidth(tabs[i]?.label, font, margin, transform) - (isXS ? (i + 1) * 4 : paddingLeft);
      }
      return width;
    }
    return 0;
  };

  const getItemTranslateValue = (index: number): string => {
    return tabs?.findIndex((tab) => tab?.label === activeTab?.label) <= index ? `translateX(${transform}px)` : 'translateX(0px)';
  };

  return (
    <>
      <Box
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          width: '100%',
        }}
        data-testid={dataTestId}
      >
        <Box
          className="custom-tab-scroll"
          style={{
            display: 'flex',
            justifyContent: 'flex-start',
            margin: '0',
            width: isXS ? '100%' : '90%',
            overflow: isXS ? 'scroll' : 'hidden',
            msOverflowStyle: 'none',
            padding: '0',
            position: 'relative',
            scrollbarWidth: 'none',
            WebkitUserSelect: 'none',
            msUserSelect: 'none',
            userSelect: 'none',
            whiteSpace: 'nowrap',
          }}
          data-testid={`${dataTestId}-scroll-container`}
        >
          {tabs?.map((collection, index) => {
            return (
              <Button
                role="tab-item"
                key={collection?.label}
                style={{
                  color: activeTab?.seName === collection?.seName ? 'black' : 'var(--light-gray)',
                  fontSize: isSmall ? '1.5rem' : '2rem',
                  height: '64px',
                  transform: getItemTranslateValue(index),
                  transition: 'transform .7s cubic-bezier(0.3, 0, 0, 1)',
                  margin: 0,
                  minWidth: 'fit-content',
                  marginRight: `${margin}px`,
                  paddingLeft: `${paddingLeft}px`,
                  fontWeight: 700,
                  outline: 'none',
                  userSelect: 'none',
                }}
                variant="white"
                onClick={function (e) {
                  onTabChange(collection);
                  if (mobile) (e.target as HTMLButtonElement).scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
                }}
                onMouseDown={(e) => {
                  (e.target as HTMLButtonElement).blur();
                }}
                data-testid={`${dataTestId}-tab-${index}-${collection?.seName}`}
              >
                {collection?.label}
              </Button>
            );
          })}

          <Box
            style={{
              position: 'absolute',
              top: isXS ? '21px' : '15px',
              left: 0,
              transition: 'transform .7s cubic-bezier(0.3, 0, 0, 1)',
              transform: `translateX(${getTranslateValue()}px)`,
            }}
            data-testid={`${dataTestId}-arrow-icon-container`}
          >
            <IconArrowRight className="aspect-square w-8 md:w-12" data-testid={`${dataTestId}-arrow-icon`} />
          </Box>
        </Box>
        {children && <div data-testid={`${dataTestId}-children-container`}>{children}</div>}
      </Box>
      <style jsx>{`
        .custom-tab-scroll::-webkit-scrollbar {
          background: rgba(0, 0, 0, 0);
          display: none;
          height: 0;
          width: 0;
        }
        .custom-tab-scroll {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
        @media (max-width: 768px) {
          .custom-tab-scroll {
            width: 100% !important;
            overflow-x: scroll !important;
          }
        }
      `}</style>
    </>
  );
};

export default CustomTab;
